import React from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  Paper,
  CircularProgress
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const MessageList = ({ messages, typing }) => {
  // Format date to a readable time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Custom components for Markdown rendering
  const components = {
    code({node, inline, className, children, ...props}) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={atomDark}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };
  
  return (
    <Box>
      {messages.map((message, index) => (
        <Box
          key={message.id || index}
          sx={{
            display: 'flex',
            mb: 3,
            alignItems: 'flex-start',
          }}
        >
          <Avatar 
            sx={{ 
              mr: 2, 
              bgcolor: message.role === 'assistant' ? 'black' : '#4B8BF5'
            }}
          >
            {message.role === 'assistant' ? <SmartToyIcon /> : <PersonIcon />}
          </Avatar>
          
          <Box sx={{ maxWidth: '80%' }}>
            <Typography 
              variant="subtitle2" 
              component="div" 
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              {message.role === 'assistant' ? 'StateShift Pathfinder' : 'You'}
              {message.created_at && (
                <Typography 
                  component="span" 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ ml: 1 }}
                >
                  {formatTime(message.created_at)}
                </Typography>
              )}
            </Typography>
            
            <Paper
              sx={{
                p: 2,
                bgcolor: message.role === 'assistant' ? '#f9f9f9' : '#e6f2ff',
                borderRadius: '10px',
                boxShadow: 'none',
              }}
            >
              {message.role === 'assistant' ? (
                <Box sx={{ '& > p:last-child': { mb: 0 } }}>
                  <ReactMarkdown components={components}>
                    {message.content}
                  </ReactMarkdown>
                </Box>
              ) : (
                <Typography variant="body1">
                  {message.content}
                </Typography>
              )}
            </Paper>
          </Box>
        </Box>
      ))}
      
      {/* Typing indicator */}
      {typing && (
        <Box
          sx={{
            display: 'flex',
            mb: 3,
            alignItems: 'flex-start',
          }}
        >
          <Avatar sx={{ mr: 2, bgcolor: 'black' }}>
            <SmartToyIcon />
          </Avatar>
          
          <Paper
            sx={{
              p: 2,
              bgcolor: '#f9f9f9',
              borderRadius: '10px',
              boxShadow: 'none',
              minWidth: 100,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} sx={{ color: 'black' }} />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MessageList;
